// src/pages/Marketing/MarketingHome.js
//
// The marketing site's one real page — hero / about / features / contact /
// demo form, in that order, built from the public content call
// (MarketingI18nContext). Every section is driven entirely by
// MarketingContentKey/Value (see seed_marketing_content.go) — nothing here
// is hardcoded English copy, so an admin editing MarketingContent.js (or
// switching the active language) is the only thing that ever changes what
// renders. A feature slot with no title is simply skipped (see
// GetPublicMarketingContentHandler's own doc comment — "fewer than 8
// filled-in features just works").
import { useState } from "react";
import { useMarketingI18n } from "./MarketingI18nContext";
import MarketingLanguageSwitcher from "./components/MarketingLanguageSwitcher";
import DemoRequestForm from "./components/DemoRequestForm";

const YEAR = new Date().getFullYear();

// UI-chrome strings that aren't part of the CMS-driven MarketingContentKey
// schema (they're page structure, not editable copy — same reasoning
// DemoRequestForm.js's own local STRINGS dict already documents). English/
// Arabic are the two seeded languages; anything else falls back to English.
const STRINGS = {
  en: {
    eyebrow: "Point of sale, run right",
    learn_more: "Learn more",
    features_heading: "Built for the floor and the back office",
    faq_heading: "Frequently asked questions",
    contact_heading: "Get in touch",
    whatsapp_label: "Chat on WhatsApp",
    call_label: "Call us",
  },
  ar: {
    eyebrow: "نقاط بيع تعمل كما ينبغي",
    learn_more: "اعرف أكثر",
    features_heading: "مصمم للعمل في الصالة وخلف الكواليس",
    faq_heading: "الأسئلة الشائعة",
    contact_heading: "تواصل معنا",
    whatsapp_label: "تواصل عبر واتساب",
    call_label: "اتصل بنا",
  },
};

// Turns whatever format an admin typed into contact.whatsapp ("+966 50 690
// 2625", "0966506902625", with spaces/dashes) into a working wa.me link, by
// stripping everything but digits — see seed_marketing_content.go's own doc
// comment on this field for why it's free-text rather than a structured
// phone field.
function whatsappLink(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

// Unlike whatsappLink (which needs bare digits for wa.me), a tel: link keeps
// a leading "+" when the admin typed one — some phone dialers rely on it to
// treat the number as already in full international format rather than a
// local one to combine with the device's own country code.
function phoneLink(raw) {
  const cleaned = (raw || "").replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : null;
}

// Every in-page anchor here (#top, #about, #demo) has the same problem: a
// plain browser anchor jump lands its target flush against the viewport's
// top edge, but .mkt-nav is position:sticky, so that edge is actually
// covered by the header — cutting off the first bit of whatever section
// just got scrolled to. .mkt-nav's own height isn't a fixed constant (it
// just pads around whatever's inside it), so it's measured live rather than
// hardcoded, then subtracted from the scroll target, plus a little extra
// breathing room so the section doesn't sit flush under the header either.
//
// The animation itself is a small custom rAF tween rather than the
// browser's own scrollTo({behavior:"smooth"}) — that native easing isn't
// something a duration/speed can be tuned on, and once the user asked for
// "a bit faster" a fixed, short SCROLL_DURATION_MS is the only way to
// actually deliver that consistently across browsers. Reduced-motion users
// still get an instant jump, same as a plain anchor would give them.
const SCROLL_GAP = 16;
const SCROLL_DURATION_MS = 350;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function scrollToSection(id) {
  const target = document.getElementById(id);
  if (!target) return false; // let the plain href="#..." jump handle it instead
  const header = document.querySelector(".mkt-nav");
  const headerHeight = header ? header.getBoundingClientRect().height : 0;
  const startY = window.pageYOffset;
  const targetY = Math.max(target.getBoundingClientRect().top + startY - headerHeight - SCROLL_GAP, 0);
  const distance = targetY - startY;

  const prefersReducedMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion || distance === 0) {
    window.scrollTo(0, targetY);
    return true;
  }

  const startTime = performance.now();
  const step = (now) => {
    const progress = Math.min((now - startTime) / SCROLL_DURATION_MS, 1);
    window.scrollTo(0, startY + distance * easeOutCubic(progress));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
  return true;
}

// Returns an onClick handler bound to one anchor's target id — used by
// every internal "#..." link below instead of letting the browser handle
// the jump on its own.
function scrollToAnchor(id) {
  return (e) => {
    if (scrollToSection(id)) e.preventDefault();
  };
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.29-.15-1.7-.84-1.96-.93-.26-.1-.46-.15-.65.15-.19.29-.75.93-.92 1.12-.17.19-.34.22-.63.07-.29-.15-1.22-.45-2.32-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.65-1.57-.89-2.15-.23-.56-.47-.48-.65-.49h-.56c-.19 0-.51.07-.78.36-.26.29-1.02 1-1.02 2.43 0 1.43 1.05 2.82 1.19 3.01.15.19 2.06 3.14 4.99 4.4.7.3 1.24.48 1.67.61.7.22 1.34.19 1.84.12.56-.08 1.7-.7 1.94-1.37.24-.68.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34z"/>
      <path d="M12.02 2C6.5 2 2.02 6.48 2.02 12c0 1.78.47 3.44 1.28 4.89L2 22l5.25-1.38A9.94 9.94 0 0 0 12.02 22C17.55 22 22 17.52 22 12S17.55 2 12.02 2zm0 18.2c-1.62 0-3.13-.47-4.4-1.28l-.32-.19-3.11.82.83-3.03-.2-.31A8.17 8.17 0 0 1 3.82 12c0-4.53 3.68-8.2 8.2-8.2 4.52 0 8.2 3.67 8.2 8.2 0 4.53-3.68 8.2-8.2 8.2z"/>
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.61 21 3 13.39 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z"/>
    </svg>
  );
}

// Real /dashboard's own user-status donut (Dashboard.js's userStats array)
// uses these exact four categories/colors — reused verbatim below instead
// of inventing a palette, so this mock's donut is recognizably the same
// widget, not a generic pie chart.
const DASHBOARD_USER_STATUS = [
  { label: "Active", value: 55, color: "#10b981" },
  { label: "Inactive", value: 20, color: "#f59e0b" },
  { label: "Banned", value: 10, color: "#ef4444" },
  { label: "Recently Out", value: 15, color: "#9ca3af" },
];

function DashboardCards() {
  // The three widget shapes rendered here — a bar chart, a donut, and a
  // timeline — are the real /dashboard's own (Dashboard.js: "Sales by
  // Branch", "User Status", "Recent Activity"), styled with the same
  // tokens the real WidgetShell/.sidebar/.header do (rounded-3xl white
  // cards, gray-100 border, shadow-sm, bold gray-800 titles) rather than a
  // generic invented UI. Every number/name is illustrative only — no real
  // order, customer, or inventory data — same status as the receipt mock's
  // own "Espresso x2 / 5.60".
  const bars = [46, 72, 58, 86, 64];
  // A radial-gradient "hole punch" layered UNDER the conic-gradient wedges
  // is what turns a plain filled circle into a donut with no extra markup
  // — both layers have to be set together here (not split between this
  // inline style and .mkt-dashboard-donut's own CSS background-image)
  // since setting the style prop replaces the whole property rather than
  // merging with it.
  const donutGradient = (() => {
    let acc = 0;
    const stops = DASHBOARD_USER_STATUS.map((s) => {
      const from = acc;
      acc += s.value;
      return `${s.color} ${from}% ${acc}%`;
    });
    return `radial-gradient(closest-side, var(--mkt-surface) 62%, transparent 63% 100%), conic-gradient(${stops.join(", ")})`;
  })();
  const activity = [
    { user: "cashier_1", event: "ORDER_CREATED", time: "2m" },
    { user: "admin", event: "PRODUCT_UPDATED", time: "14m" },
    { user: "cashier_2", event: "SHIFT_CLOSED", time: "31m" },
  ];

  return (
    <>
      <div className="mkt-dashboard-card">
        <div className="mkt-dashboard-card-head">
          <span className="mkt-dashboard-card-icon" />
          <h4>Sales by Branch</h4>
          <span className="mkt-dashboard-card-badge">Last 30 days</span>
        </div>
        <div className="mkt-dashboard-bars">
          {bars.map((h, i) => (
            <span key={i} className="mkt-dashboard-bar" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>

      <div className="mkt-dashboard-card">
        <div className="mkt-dashboard-card-head">
          <span className="mkt-dashboard-card-icon" />
          <h4>User Status</h4>
        </div>
        <div className="mkt-dashboard-donut-row">
          <span className="mkt-dashboard-donut" style={{ backgroundImage: donutGradient }} />
          <div className="mkt-dashboard-legend">
            {DASHBOARD_USER_STATUS.map((s) => (
              <span key={s.label} className="mkt-dashboard-legend-item">
                <span className="mkt-dashboard-legend-dot" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mkt-dashboard-card">
        <div className="mkt-dashboard-card-head">
          <span className="mkt-dashboard-card-icon" />
          <h4>Recent Activity</h4>
        </div>
        <div className="mkt-dashboard-timeline">
          {activity.map((a, i) => (
            <div className="mkt-dashboard-timeline-row" key={i}>
              <span className="mkt-dashboard-timeline-dot" />
              <span className="mkt-dashboard-timeline-user">{a.user}</span>
              <span className="mkt-dashboard-timeline-event">{a.event}</span>
              <span className="mkt-dashboard-timeline-time">{a.time}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function DashboardMock() {
  // A CSS/JS mockup styled to match the real /dashboard (MainLayout.css's
  // sidebar/header tokens, Dashboard.js's WidgetShell cards) rather than a
  // generic browser-chrome illustration — see DashboardCards' own doc
  // comment above for the three widgets reproduced here. mkt-dashboard-
  // scroll below renders the SAME card stack twice back to back, then a
  // CSS animation translates the whole thing up by exactly 50% of its own
  // height on a loop — since both halves are identical, that's an
  // infinite scroll with no visible seam or reset jump. The small accent
  // dot (mkt-dashboard-cursor) animates on its own loop to read as a
  // cursor scrolling the panel.
  return (
    <div className="mkt-dashboard-mock" aria-hidden="true">
      <div className="mkt-dashboard-frame">
        <div className="mkt-dashboard-topbar">
          <span className="mkt-dashboard-logo" />
          <span className="mkt-dashboard-title">Posflect</span>
        </div>
        <div className="mkt-dashboard-body">
          <div className="mkt-dashboard-sidebar">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`mkt-dashboard-navicon${i === 1 ? " active" : ""}`} />
            ))}
          </div>
          <div className="mkt-dashboard-main">
            <div className="mkt-dashboard-scroll">
              <DashboardCards />
              {/* Second, identical copy — see this component's own doc
                  comment above for why the loop needs exactly two. */}
              <DashboardCards />
            </div>
            <span className="mkt-dashboard-cursor" />
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqAccordion({ items }) {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <div className="mkt-faq-list">
      {items.map((f, i) => {
        const open = openIndex === i;
        return (
          <div className={`mkt-faq-item${open ? " open" : ""}`} key={i}>
            <button
              type="button"
              className="mkt-faq-question"
              aria-expanded={open}
              onClick={() => setOpenIndex(open ? -1 : i)}
            >
              <span>{f.question}</span>
              <span className="mkt-faq-icon" aria-hidden="true">+</span>
            </button>
            {open && f.answer && <p className="mkt-faq-answer">{f.answer}</p>}
          </div>
        );
      })}
    </div>
  );
}

function ReceiptMock() {
  // A small, purely decorative CSS "receipt" — the one visual detail this
  // hero borrows straight from the subject's own world (a POS system) —
  // rather than a generic dashboard screenshot or abstract blob art.
  return (
    <div className="mkt-receipt" aria-hidden="true">
      <div className="mkt-receipt-notch mkt-receipt-notch-top" />
      <div className="mkt-receipt-row mkt-receipt-title">POSFLECT</div>
      <div className="mkt-receipt-row mkt-receipt-sub">Order #4821 · Register 2</div>
      <div className="mkt-receipt-divider" />
      {[
        ["Espresso x2", "5.60"],
        ["Croissant", "3.20"],
        ["Iced Latte", "4.10"],
        ["Sandwich", "6.50"],
      ].map(([label, price]) => (
        <div className="mkt-receipt-row mkt-receipt-item" key={label}>
          <span>{label}</span>
          <span className="mkt-receipt-price">{price}</span>
        </div>
      ))}
      <div className="mkt-receipt-divider" />
      <div className="mkt-receipt-row mkt-receipt-total">
        <span>TOTAL</span>
        <span className="mkt-receipt-price">19.40</span>
      </div>
      <div className="mkt-receipt-barcode" />
      <div className="mkt-receipt-notch mkt-receipt-notch-bottom" />
    </div>
  );
}

export default function MarketingHome() {
  const { loading, error, content, languageCode } = useMarketingI18n();
  const t = STRINGS[languageCode] || STRINGS.en;

  if (loading && !content) {
    return (
      <div className="mkt-loading">
        <div className="mkt-loading-mark">POSFLECT</div>
      </div>
    );
  }

  if (error && !content) {
    return (
      <div className="mkt-loading">
        <p>{error}</p>
      </div>
    );
  }

  const hero = content?.hero || {};
  const about = content?.about || {};
  const features = content?.features || [];
  const faq = content?.faq || [];
  const contact = content?.contact || {};
  const waLink = whatsappLink(contact.whatsapp);
  const callLink = phoneLink(contact.phone);

  return (
    <div className="mkt-page">
      <header className="mkt-nav">
        <div className="mkt-nav-inner">
          <a href="#top" onClick={scrollToAnchor("top")} className="mkt-wordmark">Posflect</a>
          <div className="mkt-nav-actions">
            <MarketingLanguageSwitcher />
            <a href="#demo" onClick={scrollToAnchor("demo")} className="mkt-btn mkt-btn-primary mkt-btn-sm">{hero.cta_label || "Request a demo"}</a>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="mkt-hero">
          <div className="mkt-hero-inner">
            <div className="mkt-hero-copy">
              <p className="mkt-eyebrow">{t.eyebrow}</p>
              <h1 className="mkt-hero-title">{hero.title || "Posflect"}</h1>
              {hero.subtitle && <p className="mkt-hero-subtitle">{hero.subtitle}</p>}
              <div className="mkt-hero-actions">
                <a href="#demo" onClick={scrollToAnchor("demo")} className="mkt-btn mkt-btn-primary">{hero.cta_label || "Request a demo"}</a>
                <a href="#about" onClick={scrollToAnchor("about")} className="mkt-btn mkt-btn-ghost">{t.learn_more}</a>
              </div>
            </div>
            <div className="mkt-hero-art">
              <ReceiptMock />
            </div>
          </div>
        </section>

        {(about.heading || about.body) && (
          <section id="about" className="mkt-section mkt-about">
            <div className="mkt-section-inner mkt-about-inner">
              <div className="mkt-about-art">
                <DashboardMock />
              </div>
              <div className="mkt-about-copy">
                {about.heading && <h2 className="mkt-section-title">{about.heading}</h2>}
                {about.body && <p className="mkt-about-body">{about.body}</p>}
              </div>
            </div>
          </section>
        )}

        {features.length > 0 && (
          <section id="features" className="mkt-section mkt-features">
            <div className="mkt-section-inner">
              <h2 className="mkt-section-title mkt-section-title-center">{t.features_heading}</h2>
              <div className="mkt-features-grid">
                {features.map((f, i) => (
                  <article className="mkt-feature-card" key={i}>
                    <h3>{f.title}</h3>
                    {f.description && <p>{f.description}</p>}
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {faq.length > 0 && (
          <section id="faq" className="mkt-section mkt-faq">
            <div className="mkt-section-inner mkt-faq-inner">
              <h2 className="mkt-section-title">{t.faq_heading}</h2>
              <FaqAccordion items={faq} />
            </div>
          </section>
        )}

        <section id="demo" className="mkt-section mkt-demo">
          <div className="mkt-section-inner mkt-demo-inner">
            <DemoRequestForm />
          </div>
        </section>

        {(contact.email || contact.phone || contact.address || waLink) && (
          <section id="contact" className="mkt-section mkt-contact">
            <div className="mkt-section-inner mkt-contact-inner">
              <h2 className="mkt-section-title">{t.contact_heading}</h2>
              <div className="mkt-contact-grid">
                {contact.email && (
                  <a className="mkt-contact-item" href={`mailto:${contact.email}`}>{contact.email}</a>
                )}
                {contact.phone && (
                  callLink ? (
                    <a className="mkt-contact-item mkt-contact-icon-item mkt-contact-phone" href={callLink}>
                      <PhoneIcon />
                      {/* dir="ltr" only on the number itself (not the whole
                          link) — in Arabic (RTL), a plain phone/WhatsApp
                          number reorders its digits/punctuation incorrectly
                          unless explicitly isolated as left-to-right text. */}
                      <span dir="ltr">{contact.phone}</span>
                    </a>
                  ) : (
                    <span className="mkt-contact-item" dir="ltr">{contact.phone}</span>
                  )
                )}
                {waLink && (
                  <a
                    className="mkt-contact-item mkt-contact-icon-item mkt-contact-whatsapp"
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsAppIcon />
                    <span dir="ltr">{contact.whatsapp}</span>
                  </a>
                )}
                {contact.address && <span className="mkt-contact-item">{contact.address}</span>}
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="mkt-footer">
        <div className="mkt-section-inner mkt-footer-inner">
          <span>© {YEAR} Posflect</span>
        </div>
      </footer>

      {(callLink || waLink) && (
        <div className="mkt-float-stack">
          {callLink && (
            <a
              className="mkt-float-btn mkt-call-float"
              href={callLink}
              aria-label={t.call_label}
              title={t.call_label}
            >
              <PhoneIcon />
            </a>
          )}
          {waLink && (
            <a
              className="mkt-float-btn mkt-whatsapp-float"
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t.whatsapp_label}
              title={t.whatsapp_label}
            >
              <WhatsAppIcon />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
